import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Group, Stack, Text } from '@mantine/core';

import { AntibodyAnalysisPage } from '../antibody-analysis/components/AntibodyAnalysisPage';
import { AppRoutes, makeExperimentRoute } from '../appRoutes';
import { HEADER_HEIGHT } from '../constants/layout-constants';
import { useGetExperiment } from '../store/store';
import { type PageNavbarTab } from '../types/navbar';
import { capitalizeFirstLetter } from '../utils';
import { ExperimentOverviewPanel } from './ExperimentOverviewPanel';
import { LoadingContainer } from './LoadingContainer';
import { PageNavbar } from './PageNavbar';

const s = {
  contentBox: 'relative h-full min-w-0',
  hitSelectionMountActive: 'absolute inset-4 visible pointer-events-auto z-10',
  hitSelectionMountInactive: 'absolute inset-4 invisible pointer-events-none',
} as const;

// Map tabs to routes
const tabToRoute: Record<PageNavbarTab, string> = {
  'run-summary': AppRoutes.ExperimentDataOverview,
  'repertoire-summary': AppRoutes.ExperimentRepertoireAnalysis,
  'antibody-selection': AppRoutes.ExperimentAntibodyAnalysis,
  'export-summary': AppRoutes.ExperimentExportSummary,
};

// Tabs whose tables need the full content width, so the Experiment overview panel starts closed.
const FULL_WIDTH_TABS: PageNavbarTab[] = ['antibody-selection', 'export-summary'];

// Map routes to tabs
const routeToTab: Record<string, PageNavbarTab> = {
  [AppRoutes.ExperimentDataOverview]: 'run-summary',
  [AppRoutes.ExperimentRepertoireAnalysis]: 'repertoire-summary',
  [AppRoutes.ExperimentAntibodyAnalysis]: 'antibody-selection',
  [AppRoutes.ExperimentExportSummary]: 'export-summary',
};

export function AppWithSidebarLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: experiment, error } = useGetExperiment({
    id: id!,
  });

  // Determine active tab from current route
  const activeTab = React.useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const currentRoute = pathSegments[pathSegments.length - 1] ?? '';
    return routeToTab[currentRoute] || 'run-summary';
  }, [location.pathname]);

  // Panel defaults OPEN on the summary tabs, CLOSED on the wide-table tabs (see FULL_WIDTH_TABS).
  const defaultShowPanel = !FULL_WIDTH_TABS.includes(activeTab);
  const [panelOverride, setPanelOverride] = React.useState<boolean | null>(null);
  const [lastTab, setLastTab] = React.useState(activeTab);
  if (lastTab !== activeTab) {
    setLastTab(activeTab);
    setPanelOverride(null);
  }
  const showOverviewPanel = panelOverride ?? defaultShowPanel;

  const handleTabChange = React.useCallback(
    (tab: PageNavbarTab) => {
      if (id) {
        const route = tabToRoute[tab];
        navigate(makeExperimentRoute(id, route));
      }
    },
    [id, navigate],
  );

  const navigateToExperimentsOverview = React.useCallback(() => {
    navigate(AppRoutes.ExperimentOverview);
  }, [navigate]);

  const handleCloseOverviewPanel = React.useCallback(() => {
    setPanelOverride(false);
  }, []);

  const handleToggleOverviewPanel = React.useCallback(() => {
    setPanelOverride((prev) => !(prev ?? defaultShowPanel));
  }, [defaultShowPanel]);

  if (error && 'data' in error && 'detail' in (error as any).data) {
    return (
      <Stack flex={1} mih={500} justify="center" align="center">
        <Text data-testid="experiment-not-found">Experiment not found</Text>
        <Button onClick={navigateToExperimentsOverview}>Experiments overview</Button>
      </Stack>
    );
  }
  if (!experiment) {
    return (
      <Stack flex={1} mih={500} justify="center" align="center">
        <LoadingContainer text="Hang in there, we are initializing the experiment..." />
      </Stack>
    );
  }

  if (experiment.analysisState !== 'SUCCESS') {
    return (
      <Stack flex={1} mih={500} justify="center" align="center">
        <Text>Experiment unavailable because the analysis {experiment.analysisState === 'FAILURE' ? 'failed' : 'is in progress, please come back later!'}</Text>
        <Button onClick={navigateToExperimentsOverview}>Experiments overview</Button>
      </Stack>
    );
  }

  return (
    <Stack gap={0} h={`calc(100vh - ${HEADER_HEIGHT}px)`} style={{ overflow: 'hidden' }}>
      <PageNavbar
        experimentName={capitalizeFirstLetter(experiment.title)}
        experimentOrganism={capitalizeFirstLetter(experiment.organism)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onHomeClick={navigateToExperimentsOverview}
        onExperimentClick={handleToggleOverviewPanel}
      />
      <Group flex={1} p="md" gap="md" align="flex-start" style={{ overflowY: 'auto', scrollbarGutter: 'stable', position: 'relative' }} wrap="nowrap" h="100%">
        {showOverviewPanel && <ExperimentOverviewPanel experimentId={id!} onClose={handleCloseOverviewPanel} />}
        <Box flex={1} className={s.contentBox}>
          <Outlet />
        </Box>
        {/* Hit Selection: permanently mounted the moment the user enters any experiment view so
            its ECharts network graphs initialise once and stay alive across tab switches. */}
        <div className={activeTab === 'antibody-selection' ? s.hitSelectionMountActive : s.hitSelectionMountInactive}>
          <AntibodyAnalysisPage />
        </div>
      </Group>
    </Stack>
  );
}
